import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Box, VStack, HStack, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import CollapsibleLeftNav from "components/common/CollapsibleLeftNav";
import AgentTable from "modules/eva/components/AgentTable";
import ToolTable from "modules/eva/components/ToolTable";
import ExperimentTable from "modules/eva/components/ExperimentTable";
import SkillTable from "modules/eva/components/SkillTable";
import EvaPlayground from "modules/eva/containers/EvaPlayground";
import UserManagement from "modules/eva/containers/UserManagement";
import UsageMetricsContainer from "modules/eva/containers/UsageMetricsContainer";
import ChatPage from "pages/chat/ChatPage";
import { ChatClientType } from "build-ts/chat_session";
import { DarwinHeader, SortBy, SortOrder } from "build-ts/common";
import { DarwinAgentType } from "build-ts/common";
import { AppIcons } from "constants/icons";
import { useUser } from "context/UserContext";
import { fetchListOfChatSessions, deleteChatSession as deleteChatSessionAPI } from "services/AgentChatService";
import { uploadReportTemplate, downloadDocTemplate, deleteReportTemplate } from "services/EvaServices";
import showToast from "hooks/useCustomToast";
import { formatBytes } from "utils/helperFuncUtils";
import ReportTemplateTab from "../components/ReportTemplateTab";

const EvaList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useUser();

  // EVA session list — local state, same pattern as Navbar's FRIDA session list.
  // No need to hoist into ChatContext: only this component reads/writes it.
  const [evaChatSessionsList, setEvaChatSessionsList] = useState<DarwinHeader[]>([]);
  const [selectedEvaSessionId, setSelectedEvaSessionId] = useState<string>("");
  const [isLoadingEvaSessions, setIsLoadingEvaSessions] = useState(false);
  const [isUploadingTemplate, setIsUploadingTemplate] = useState(false);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);

  const fetchEvaChatSessionsList = useCallback(
    async (searchQuery?: string) => {
      setIsLoadingEvaSessions(true);
      try {
        const result = await fetchListOfChatSessions(
          [ChatClientType.EVA],
          100,
          0,
          searchQuery,
          "",
          undefined,
          SortBy.LAST_MODIFIED_DATE,
          SortOrder.DESC,
          userId
        );
        setEvaChatSessionsList(result.headers);
      } catch (error) {
        showToast("Failed to fetch EVA chat sessions", "", "error");
        console.error("Failed to fetch EVA chat sessions:", error);
      } finally {
        setIsLoadingEvaSessions(false);
      }
    },
    [userId]
  );

  const deleteEvaSession = useCallback(
    async (sessionId: string) => {
      try {
        await deleteChatSessionAPI(sessionId);
        if (selectedEvaSessionId === sessionId) {
          setSelectedEvaSessionId("");
          // Navigate away so ChatPage doesn't keep showing the deleted session
          navigate("/eva/agents");
        }
        await fetchEvaChatSessionsList("");
      } catch (error) {
        showToast("Failed to delete chat session", "", "error");
        console.error("Error deleting EVA chat session:", error);
      }
    },
    [selectedEvaSessionId, fetchEvaChatSessionsList, navigate]
  );

  const handleUploadTemplate = useCallback(async (file: File) => {
    setIsUploadingTemplate(true);
    try {
      const { size_bytes } = await uploadReportTemplate(file);
      showToast(
        "Template uploaded",
        `${file.name} (${formatBytes(size_bytes)}) is now active for all new reports.`,
        "success"
      );
    } finally {
      setIsUploadingTemplate(false);
    }
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      await downloadDocTemplate();
    } catch (error: any) {
      showToast("Download failed", error?.message || "Could not download the current template.", "error");
    }
  }, []);

  const handleDeleteTemplate = useCallback(async () => {
    setIsDeletingTemplate(true);
    try {
      await deleteReportTemplate();
      showToast(
        "Template removed",
        "Reports will use the default plain style until a new template is uploaded.",
        "success"
      );
    } catch (error: any) {
      showToast("Remove failed", error?.message || "Could not remove the current template.", "error");
    } finally {
      setIsDeletingTemplate(false);
    }
  }, []);

  const routeState = location.state as
    | undefined
    | {
        initialDarwinAgentId?: string;
        initialDarwinAgentType?: DarwinAgentType;
        initialPromptId?: string;
        startAtStepIndex?: number;
      };

  const activeSection = useMemo(() => {
    const pathname = location.pathname;
    if (pathname.startsWith("/eva/chat")) {
      return "chatSessions";
    } else if (pathname.startsWith("/eva/playground")) {
      return "playground";
    } else if (pathname.startsWith("/eva/tools")) {
      return "tools";
    } else if (pathname.startsWith("/eva/experiments")) {
      return "experiments";
    } else if (pathname.startsWith("/eva/skills")) {
      return "skills";
    } else if (pathname.startsWith("/eva/users")) {
      return "users";
    } else if (pathname.startsWith("/eva/report-template")) {
      return "reportTemplate";
    } else if (pathname.startsWith("/eva/usage-metrics")) {
      return "usageMetrics";
    } else if (pathname.startsWith("/eva/agents")) {
      return "agents";
    }
    return "agents";
  }, [location.pathname]);

  // Fetch EVA sessions on mount
  useEffect(() => {
    fetchEvaChatSessionsList("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync selected session ID with URL; clear when navigating away from chat
  useEffect(() => {
    const pathname = location.pathname;
    if (pathname.startsWith("/eva/chat/")) {
      const chatId = pathname.split("/eva/chat/")[1];
      if (chatId && chatId !== selectedEvaSessionId) {
        setSelectedEvaSessionId(chatId);
      }
    } else if (selectedEvaSessionId) {
      setSelectedEvaSessionId("");
    }
  }, [location.pathname, selectedEvaSessionId]);

  const handleCreate = () => navigate("/eva/agent/create");
  const handleCreateSkill = () => navigate("/eva/skill/create");
  const handleAgentsClick = () => navigate("/eva/agents");
  const handleToolsClick = () => navigate("/eva/tools");
  const handlePlaygroundClick = () => navigate("/eva/playground");
  const handleExperimentsClick = () => navigate("/eva/experiments");
  const handleSkillsClick = () => navigate("/eva/skills");
  const handleUsersClick = () => navigate("/eva/users");
  const handleReportTemplateClick = () => navigate("/eva/report-template");
  const handleUsageMetricsClick = () => navigate("/eva/usage-metrics");

  const handleSessionSelect = (sessionId: string) => navigate(`/eva/chat/${sessionId}`);
  const handleSessionDelete = (sessionId: string) => deleteEvaSession(sessionId);

  const navItems = [
    { id: "agents", label: "Agents", icon: AppIcons.agentBot, iconBoxSize: 6, onClick: handleAgentsClick },
    { id: "tools", label: "Tools", icon: AppIcons.tools, iconBoxSize: 5, onClick: handleToolsClick },
    { id: "skills", label: "Skills", icon: AppIcons.loadSkill, iconBoxSize: 5, onClick: handleSkillsClick },
    {
      id: "playground",
      label: "Playground",
      icon: AppIcons.playground,
      iconBoxSize: 6,
      onClick: handlePlaygroundClick,
    },
    { id: "experiments", label: "Experiments", icon: AppIcons.flask, iconBoxSize: 5, onClick: handleExperimentsClick },
    {
      id: "reportTemplate",
      label: "Upload Template",
      icon: AppIcons.uploadOutline,
      iconBoxSize: 6,
      onClick: handleReportTemplateClick,
    },
    { id: "users", label: "Users", icon: AppIcons.users, iconBoxSize: 5, onClick: handleUsersClick },
    {
      id: "usageMetrics",
      label: "Usage Metrics",
      icon: AppIcons.evaUsageMetrics,
      iconBoxSize: 5,
      onClick: handleUsageMetricsClick,
    },
  ];

  return (
    <Flex height="100%" gap={0.5}>
      <CollapsibleLeftNav
        title="Eva"
        titleIcon={AppIcons.evaAgent}
        items={navItems}
        activeItemId={activeSection}
        defaultWidth={200}
        minWidth={180}
        maxWidth={350}
        isFromEvaPlayground={true}
        sessions={evaChatSessionsList}
        selectedSessionId={selectedEvaSessionId}
        onSessionSelect={handleSessionSelect}
        onSessionDelete={handleSessionDelete}
        onSessionsRefresh={() => fetchEvaChatSessionsList("")}
        isLoadingSessions={isLoadingEvaSessions}
      />

      <Box bg="bg.surface" boxShadow="lg" borderRadius="lg" overflowY="hidden" flex="1">
        {activeSection === "agents" && (
          <VStack className="eva-list-content" spacing={3} p={5} height="100%" alignItems="stretch">
            <HStack justify="space-between" w="full" px={3} flexShrink={0}>
              <HStack spacing={4}>
                <Icon as={AppIcons.agentBot} boxSize={8} color="fg.default" />
                <Text fontSize="lg" mb={0} fontWeight="700">
                  Manage Agents
                </Text>
              </HStack>
              <Button
                onClick={handleCreate}
                colorScheme="purple"
                size="md"
                bg="purple.700"
                color="white"
                _hover={{ bg: "purple.600" }}
                leftIcon={<Icon as={AppIcons.add} boxSize={3} color="white" />}
              >
                New Agent
              </Button>
            </HStack>
            <Box flex="1" overflow="hidden" width="100%">
              <AgentTable />
            </Box>
          </VStack>
        )}

        {activeSection === "tools" && (
          <VStack className="eva-list-content" spacing={3} p={5} height="100%" alignItems="stretch">
            <HStack justify="space-between" w="full" px={3} flexShrink={0}>
              <HStack spacing={4}>
                <Icon as={AppIcons.tools} boxSize={7} color="fg.default" />
                <Text fontSize="lg" mb={0} fontWeight="700">
                  Manage Tools
                </Text>
              </HStack>
            </HStack>
            <Box flex="1" overflow="hidden" width="100%">
              <ToolTable />
            </Box>
          </VStack>
        )}

        {activeSection === "experiments" && (
          <VStack className="eva-list-content" spacing={3} p={5} height="100%" alignItems="stretch">
            <HStack justify="space-between" w="full" px={3} flexShrink={0}>
              <HStack spacing={4}>
                <Icon as={AppIcons.flask} boxSize={7} color="fg.default" />
                <Text fontSize="lg" mb={0} fontWeight="700">
                  Manage Experiments
                </Text>
              </HStack>
            </HStack>
            <Box flex="1" overflow="hidden" width="100%">
              <ExperimentTable />
            </Box>
          </VStack>
        )}

        {activeSection === "skills" && (
          <VStack className="eva-list-content" spacing={3} p={5} height="100%" alignItems="stretch">
            <HStack justify="space-between" w="full" px={3} flexShrink={0}>
              <HStack spacing={4}>
                <Icon as={AppIcons.loadSkill} boxSize={7} color="fg.default" />
                <Text fontSize="lg" mb={0} fontWeight="700">
                  Manage Skills
                </Text>
              </HStack>
              <Button
                onClick={handleCreateSkill}
                colorScheme="purple"
                size="md"
                bg="purple.700"
                color="white"
                _hover={{ bg: "purple.600" }}
                leftIcon={<Icon as={AppIcons.add} boxSize={3} color="white" />}
              >
                New Skill
              </Button>
            </HStack>
            <Box flex="1" overflow="hidden" width="100%">
              <SkillTable />
            </Box>
          </VStack>
        )}

        {activeSection === "users" && <UserManagement />}

        {activeSection === "reportTemplate" && (
          <VStack className="eva-list-content" spacing={3} p={5} height="100%" alignItems="stretch">
            <HStack justify="space-between" w="full" px={3} flexShrink={0}>
              <HStack spacing={4}>
                <Icon as={AppIcons.uploadOutline} boxSize={7} color="fg.default" />
                <Text fontSize="lg" mb={0} fontWeight="700">
                  Upload Template
                </Text>
              </HStack>
            </HStack>
            <Box flex="1" overflow="auto" width="100%">
              <ReportTemplateTab
                onUpload={handleUploadTemplate}
                onDelete={handleDeleteTemplate}
                isDeleting={isDeletingTemplate}
                isUploading={isUploadingTemplate}
                onDownload={handleDownloadTemplate}
              />
            </Box>
          </VStack>
        )}

        {activeSection === "usageMetrics" && <UsageMetricsContainer />}

        {activeSection === "playground" && (
          <EvaPlayground
            initialDarwinAgentId={routeState?.initialDarwinAgentId}
            initialDarwinAgentType={routeState?.initialDarwinAgentType}
            initialPromptId={routeState?.initialPromptId}
            startAtStepIndex={routeState?.startAtStepIndex}
            userId={userId}
            onEvaSessionsRefresh={() => fetchEvaChatSessionsList("")}
          />
        )}

        {activeSection === "chatSessions" && (
          <ChatPage
            isFromEva={true}
            initialChatSessionHeader={evaChatSessionsList.find(session => session.id === selectedEvaSessionId)}
            onEvaSessionsRefresh={() => fetchEvaChatSessionsList("")}
          />
        )}
      </Box>
    </Flex>
  );
};

export default EvaList;
